import React, { useEffect, useState } from "react";
import {
  Search,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader,
} from "lucide-react";
import { useMemo } from "react";
import { adminUserService } from "../../../services/api/admin.user.service";

// Real API call to fetch watchlists
const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

// Real API call to fetch watchlists
const fetchUserWatchlists = async (page = 1, limit = 5) => {
  try {
    const token = getAuthToken(); // from localStorage or utility
    const data = await adminUserService.getAllUserWatchlists(
      page,
      limit,
      token
    );
    return data;
  } catch (error) {
    console.error("Error fetching watchlists:", error);
    return {
      success: false,
      data: [],
      pagination: { total: 0, page: 1, pages: 1 },
    };
  }
};
export default function UserWatchlists() {
  const [watchlistData, setWatchlistData] = useState([]);
 
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("fullName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    const loadWatchlists = async () => {
      setLoading(true);
      const result = await fetchUserWatchlists(currentPage, itemsPerPage);

      if (result.success) {
        setWatchlistData(result.data);
        setTotalItems(result.pagination.total);
        setTotalPages(result.pagination.pages);
      } else {
        setWatchlistData([]);
      }

      setLoading(false);
    };

    loadWatchlists();
  }, [currentPage, itemsPerPage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ArrowUp className="w-4 h-4 ml-1" />
    ) : (
      <ArrowDown className="w-4 h-4 ml-1" />
    );
  };

  const processedData = useMemo(() => {
    const userMap = new Map();

    watchlistData.forEach((watchlist) => {
      const userId = watchlist.userId?._id || watchlist.userId?.toString();

      if (!userMap.has(userId)) {
        userMap.set(userId, {
          id: userId,
          user: watchlist.userId,
          products: [],
          watchlistCount: 0,
        });
      }

      const userEntry = userMap.get(userId);
      userEntry.products.push(...(watchlist.products || []));
      userEntry.watchlistCount += 1;
    });

    return Array.from(userMap.values());
  }, [watchlistData]);
  // Filter by search term (client-side filtering)
  const filtered = processedData.filter((item) => {
    const searchTerm = search.toLowerCase();
    const userName = item.user?.fullName || "";
    const userEmail = item.user?.email || "";

    return (
      userName.toLowerCase().includes(searchTerm) ||
      userEmail.toLowerCase().includes(searchTerm)
    );
  });

  // Sort data (client-side sorting)
  const sorted = [...filtered].sort((a, b) => {
    let valA, valB;

    if (sortField === "fullName") {
      valA = a.user?.fullName?.toLowerCase() || "";
      valB = b.user?.fullName?.toLowerCase() || "";
    } else if (sortField === "email") {
      valA = a.user?.email?.toLowerCase() || "";
      valB = b.user?.email?.toLowerCase() || "";
    } else {
      valA = a[sortField]?.toString().toLowerCase() || "";
      valB = b[sortField]?.toString().toLowerCase() || "";
    }

    if (valA < valB) return sortDirection === "asc" ? -1 : 1;
    if (valA > valB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            User Watchlists
          </h2>
          <p className="text-sm text-gray-500">
            List of stocks each user is watching
          </p>
        </div>
        <div className="relative w-72">
          <input
            type="text"
            placeholder="Search user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          <Search className="absolute top-2.5 left-3 h-5 w-5 text-gray-400" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader className="animate-spin text-indigo-500 mb-4" size={36} />{" "}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort("fullName")}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  >
                    <div className="flex items-center">
                      Name
                      {sortIcon("fullName")}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort("email")}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  >
                    <div className="flex items-center">
                      Email
                      {sortIcon("email")}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Watchlist
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sorted.length > 0 ? (
                  sorted.map((item) => (
                    <tr
                      key={`${item.id}-${item.user?._id || "no-user"}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.user?.fullName || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.user?.email || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.user?.mobileNo || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="flex flex-wrap gap-2">
                          {item.products.map((product) => (
                            <span
                              key={product._id || `${item.id}-product-${index}`}
                              className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded"
                            >
                              {product.productId?.basicData?.uniqueTitle ||
                                product.productId?.basicData?.legalName ||
                                "Unknown"}
                              <Eye className="ml-1 h-3 w-3" />
                            </span>
                          ))}
                          {item.products.length === 0 && (
                            <span className="text-gray-400">
                              No items in watchlist
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No watchlists found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex justify-between items-center border-t pt-4">
            <p className="text-sm text-gray-600">
              Showing{" "}
              {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{" "}
              {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
              results
            </p>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className={`px-3 py-1 border rounded-md ${
                  currentPage === 1
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className={`px-3 py-1 border rounded-md ${
                  currentPage === totalPages
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
